import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, Camera, ScanLine, Loader2 } from 'lucide-react';

export default function QRScanner({ onScan, onClose }) {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScanner = async () => {
    setError(null);
    setIsScanning(true);

    try {
      // Check for camera permission
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);

      // Initialize scanner
      html5QrCodeRef.current = new Html5Qrcode("qr-reader");
      
      await html5QrCodeRef.current.start(
        { facingMode: "environment" }, // Use back camera
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0
        },
        (decodedText) => {
          // Success - QR code scanned
          console.log('QR Code scanned:', decodedText);
          
          // Stop scanner
          html5QrCodeRef.current.stop().catch(() => {});
          setIsScanning(false);
          
          // Extract asset ID from URL if it's a full URL
          let assetId = decodedText;
          if (decodedText.includes('/asset/')) {
            assetId = decodedText.split('/asset/').pop();
          }
          
          onScan(assetId);
        },
        (errorMessage) => {
          // Ignore scan errors (happens every frame without QR)
        }
      );
    } catch (err) {
      console.error('Scanner error:', err);
      setIsScanning(false);
      
      if (err.name === 'NotAllowedError') {
        setHasPermission(false);
        setError('Camera permission denied. Please allow camera access to scan QR codes.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Failed to start camera. Please try again.');
      }
    }
  };

  const stopScanner = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().catch(() => {});
    }
    setIsScanning(false);
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" data-testid="qr-scanner">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-900">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <ScanLine className="w-5 h-5" />
          Scan Asset QR Code
        </h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            stopScanner();
            onClose();
          }}
          className="text-white hover:bg-slate-800"
        >
          <X className="w-5 h-5" />
        </Button>
      </div>

      {/* Scanner Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {!isScanning ? (
          <Card className="w-full max-w-sm">
            <CardContent className="p-6 text-center">
              {error ? (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-red-600 mb-4">{error}</p>
                  <Button onClick={startScanner} className="w-full">
                    Try Again
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-8 h-8 text-amber-600" />
                  </div>
                  <p className="text-slate-600 mb-4">
                    Point your camera at an asset's QR code to quickly access it
                  </p>
                  <Button 
                    onClick={startScanner} 
                    className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900"
                    data-testid="start-scanner-btn"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    Start Camera
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="w-full max-w-sm">
            {/* QR Scanner View */}
            <div 
              id="qr-reader" 
              ref={scannerRef}
              className="w-full rounded-lg overflow-hidden"
              style={{ minHeight: '300px' }}
            />
            
            {/* Scanning indicator */}
            <div className="mt-4 text-center">
              <div className="flex items-center justify-center gap-2 text-white">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Scanning...</span>
              </div>
              <p className="text-slate-400 text-sm mt-2">
                Position the QR code within the frame
              </p>
            </div>

            {/* Cancel button */}
            <Button
              variant="outline"
              onClick={stopScanner}
              className="w-full mt-4 bg-white"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="p-4 bg-slate-900 text-center">
        <p className="text-slate-400 text-sm">
          Scan the QR code on any company asset to check it out or in
        </p>
      </div>
    </div>
  );
}
